import type {
  ParamProvenance,
  SimMeasurableParam,
  SimProfile,
  SimProvenance,
} from "../api-contracts/scenarios.js";

// Origem por parâmetro com retrocompat do escalar legado.
// Snapshots antigos só tinham `provenance`/`sampleCount` escalares no perfil inteiro, e a única
// derivação medida existente era MTBF/MTTR — logo o escalar legado projeta APENAS em mtbf/mttr;
// feedRate/quality legados são sempre premissa (`manual`).

/** Piso de volume produzido na janela p/ feedRate/quality virarem "measured"
 * (clarificação 2026-07-09). Fonte única — consumida pela API (measured-production) e pelo
 * worker (derive-production); nunca duplicar o literal. */
export const MIN_PRODUCED_FOR_MEASURED = 100;

/** Subconjunto do SimProfile relevante p/ origem (aceita perfis parciais/efêmeros — todos os
 * campos opcionais; originOf usa optional-chaining e trata ausência como premissa). */
export type ProvenanceSource = Partial<
  Pick<SimProfile, "provenance" | "sampleCount" | "provenanceByParam">
>;

/** Origem efetiva de um parâmetro: mapa por-param quando presente; senão fallback do escalar
 * legado (que só projeta em mtbf/mttr). Perfil ausente ⇒ premissa. */
export function originOf(
  profile: ProvenanceSource | undefined,
  param: SimMeasurableParam
): ParamProvenance {
  const perParam = profile?.provenanceByParam?.[param];
  if (perParam) return perParam;

  const scalar = profile?.provenance;
  const legacyDerivable = param === "mtbf" || param === "mttr";
  if (legacyDerivable && (scalar === "measured" || scalar === "similar" || scalar === "spec")) {
    return { provenance: scalar, sampleCount: profile?.sampleCount };
  }
  return { provenance: "manual" };
}

/** Re-derivação ao vivo de um parâmetro (a janela de calibração é a fonte da verdade),
 * usada de forma idêntica pelo preview do server e pela rodada detalhada p/ não divergirem:
 *  - valor MEDIDO ao vivo presente ⇒ vence (origem `measured`);
 *  - salvo era `measured` mas a janela não mede mais ⇒ **DEMOVE** (valor `undefined` ⇒ o consumidor
 *    aplica o default/premissa; origem `manual`) — um snapshot medido obsoleto nunca é reusado;
 *  - salvo `manual`/`similar` ⇒ preserva valor e origem (autoria explícita).
 */
export function reconcileMeasuredParam(
  liveValue: number | undefined,
  liveSampleCount: number | undefined,
  savedValue: number | undefined,
  savedProvenance: SimProvenance
): { value: number | undefined; provenance: ParamProvenance } {
  if (liveValue !== undefined) {
    return {
      value: liveValue,
      provenance: {
        provenance: "measured",
        ...(liveSampleCount !== undefined && { sampleCount: liveSampleCount }),
      },
    };
  }
  if (savedProvenance === "measured") {
    return { value: undefined, provenance: { provenance: "manual" } };
  }
  return { value: savedValue, provenance: { provenance: savedProvenance } };
}
